import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import {
  FetchCompanyWithdrawRequestsResponse,
  FetchCompanyWithdrawRequestResponse,
  CreateCompanyWithdrawRequestResponse,
  UpdateCompanyWithdrawRequestResponse,
  ApproveCompanyWithdrawRequestResponse,
  RejectCompanyWithdrawRequestResponse,
} from '../../../../types/b2tRatibiPlus';
import {
  approveCompany,
  restoreCompany,
  createCompany,
  createDocumentType,
  deleteCompany,
  deleteCompanyDocument,
  fetchCompanies,
  fetchCompany,
  fetchCompanyDocuments,
  fetchCompanyStatuses,
  fetchDocumentType,
  getAttachment,
  updateCompany,
  updateCompanyDocument,
  uploadCompanyDocument,
  resendActivationLink,
  fetchCompanyWithdrawRequests,
  fetchCompanyWithdrawRequest,
  createCompanyWithdrawRequest,
  updateCompanyWithdrawRequest,
  approveCompanyWithdrawRequest,
  rejectCompanyWithdrawRequest,
} from './company.actions';
import { CompanyState } from './company.types';

const initialState: CompanyState = {
  companies: [],
  totalCompanies: 0,
  company: {},
  documentTypes: [],
  documentTypesTotal: 0,
  createDocumentTypeStatus: fetchStatuses.idle,
  companyStatuses: [],
  companyDocuments: [],
  companyWithdrawRequests: [],
  totalCompanyWithdrawRequests: 0,
  fetchCompaniesStatus: fetchStatuses.idle,
  fetchCompanyStatus: fetchStatuses.idle,
  createCompanyStatus: fetchStatuses.idle,
  updateCompanyStatus: fetchStatuses.idle,
  deleteCompanyStatus: fetchStatuses.idle,
  fetchDocumentTypeStatus: fetchStatuses.idle,
  fetchCompanyStatusesStatus: fetchStatuses.idle,
  approveCompanyStatus: fetchStatuses.idle,
  restoreCompanyStatus: fetchStatuses.idle,
  uploadDocumentStatus: fetchStatuses.idle,
  updateDocumentStatus: fetchStatuses.idle,
  fetchCompanyDocumentsStatus: fetchStatuses.idle,
  deleteDocumentStatus: fetchStatuses.idle,
  getAttachmentStatus: fetchStatuses.idle,
  resendActivationLinkStatus: fetchStatuses.idle,
  fetchCompanyWithdrawRequestsStatus: fetchStatuses.idle,
  fetchCompanyWithdrawRequestStatus: fetchStatuses.idle,
  createCompanyWithdrawRequestStatus: fetchStatuses.idle,
  updateCompanyWithdrawRequestStatus: fetchStatuses.idle,
  approveCompanyWithdrawRequestStatus: fetchStatuses.idle,
  rejectCompanyWithdrawRequestStatus: fetchStatuses.idle,
  editCompanyWithdrawRequest: null,
  deletingDocumentId: null,
  lastResendActivationLinkResponse: undefined,

  error: null,
};

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // === FETCH COMPANIES ===
    builder.addCase(fetchCompanies.pending, (state) => {
      state.error = null;
      state.fetchCompaniesStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCompanies.fulfilled, (state, action) => {
      state.fetchCompaniesStatus = fetchStatuses.success;
      state.companies = action.payload.data?.companies ?? [];
      state.totalCompanies = action.payload.data?.total ?? 0;
    });
    builder.addCase(fetchCompanies.rejected, (state, action) => {
      state.fetchCompaniesStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH COMPANY ===
    builder.addCase(fetchCompany.pending, (state) => {
      state.error = null;
      state.fetchCompanyStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCompany.fulfilled, (state, action) => {
      state.fetchCompanyStatus = fetchStatuses.success;
      state.company = action.payload.data;
    });
    builder.addCase(fetchCompany.rejected, (state, action) => {
      state.fetchCompanyStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === CREATE COMPANY ===
    builder.addCase(createCompany.pending, (state) => {
      state.error = null;
      state.createCompanyStatus = fetchStatuses.pending;
    });
    builder.addCase(createCompany.fulfilled, (state) => {
      state.createCompanyStatus = fetchStatuses.success;
    });
    builder.addCase(createCompany.rejected, (state, action) => {
      state.createCompanyStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPDATE COMPANY ===
    builder.addCase(updateCompany.pending, (state) => {
      state.error = null;
      state.updateCompanyStatus = fetchStatuses.pending;
    });
    builder.addCase(updateCompany.fulfilled, (state) => {
      state.updateCompanyStatus = fetchStatuses.success;
    });
    builder.addCase(updateCompany.rejected, (state, action) => {
      state.updateCompanyStatus = fetchStatuses.rejected;
      state.error = action.error;
    });
    // === DELETE COMPANY ===
    builder.addCase(deleteCompany.pending, (state) => {
      state.error = null;
      state.deleteCompanyStatus = fetchStatuses.pending;
    });
    builder.addCase(deleteCompany.fulfilled, (state) => {
      state.deleteCompanyStatus = fetchStatuses.success;
    });
    builder.addCase(deleteCompany.rejected, (state, action) => {
      state.deleteCompanyStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === RESEND ACTIVATION LINK ===
    // === FETCH DOCUMENT TYPE ===
    builder.addCase(fetchDocumentType.pending, (state) => {
      state.error = null;
      state.fetchDocumentTypeStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchDocumentType.fulfilled, (state, action) => {
      state.fetchDocumentTypeStatus = fetchStatuses.success;
      state.documentTypes = action.payload.data?.documentTypes ?? [];
      state.documentTypesTotal = action.payload.data?.total ?? state.documentTypes.length;
    });
    builder.addCase(fetchDocumentType.rejected, (state, action) => {
      state.fetchDocumentTypeStatus = fetchStatuses.rejected;
      state.documentTypesTotal = state.documentTypes.length;
      state.error = action.error;
    });

    builder.addCase(createDocumentType.pending, (state) => {
      state.error = null;
      state.createDocumentTypeStatus = fetchStatuses.pending;
    });
    builder.addCase(createDocumentType.fulfilled, (state, action) => {
      state.createDocumentTypeStatus = fetchStatuses.success;
      const createdDocumentType = action.payload.data;

      if (createdDocumentType?.id) {
        const exists = state.documentTypes.some((docType) => docType.id === createdDocumentType.id);

        if (!exists) {
          state.documentTypes = [createdDocumentType, ...state.documentTypes];
          state.documentTypesTotal += 1;
        }
      }
    });
    builder.addCase(createDocumentType.rejected, (state, action) => {
      state.createDocumentTypeStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH COMPANY STATUSES ===
    builder.addCase(fetchCompanyStatuses.pending, (state) => {
      state.error = null;
      state.fetchCompanyStatusesStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCompanyStatuses.fulfilled, (state, action) => {
      state.fetchCompanyStatusesStatus = fetchStatuses.success;
      state.companyStatuses = action.payload.data?.companyStatuses ?? [];
    });
    builder.addCase(fetchCompanyStatuses.rejected, (state, action) => {
      state.fetchCompanyStatusesStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === APPROVE COMPANY ===
    builder.addCase(approveCompany.pending, (state) => {
      state.error = null;
      state.approveCompanyStatus = fetchStatuses.pending;
    });
    builder.addCase(approveCompany.fulfilled, (state) => {
      state.approveCompanyStatus = fetchStatuses.success;
    });
    builder.addCase(approveCompany.rejected, (state, action) => {
      state.approveCompanyStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === RESTORE COMPANY ===
    builder.addCase(restoreCompany.pending, (state) => {
      state.error = null;
      state.restoreCompanyStatus = fetchStatuses.pending;
    });
    builder.addCase(restoreCompany.fulfilled, (state) => {
      state.restoreCompanyStatus = fetchStatuses.success;
    });
    builder.addCase(restoreCompany.rejected, (state, action) => {
      state.restoreCompanyStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPLOAD COMPANY DOCUMENT ===
    builder.addCase(uploadCompanyDocument.pending, (state) => {
      state.error = null;
      state.uploadDocumentStatus = fetchStatuses.pending;
    });
    builder.addCase(uploadCompanyDocument.fulfilled, (state) => {
      state.uploadDocumentStatus = fetchStatuses.success;
    });
    builder.addCase(uploadCompanyDocument.rejected, (state, action) => {
      state.uploadDocumentStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPDATE COMPANY DOCUMENT ===
    builder.addCase(updateCompanyDocument.pending, (state) => {
      state.error = null;
      state.updateDocumentStatus = fetchStatuses.pending;
    });
    builder.addCase(updateCompanyDocument.fulfilled, (state) => {
      state.updateDocumentStatus = fetchStatuses.success;
    });
    builder.addCase(updateCompanyDocument.rejected, (state, action) => {
      state.updateDocumentStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH COMPANY DOCUMENTS ===
    builder.addCase(fetchCompanyDocuments.pending, (state) => {
      state.error = null;
      state.fetchCompanyDocumentsStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCompanyDocuments.fulfilled, (state, action) => {
      state.fetchCompanyDocumentsStatus = fetchStatuses.success;
      state.companyDocuments = action.payload.data?.documents ?? [];
    });
    builder.addCase(fetchCompanyDocuments.rejected, (state, action) => {
      state.fetchCompanyDocumentsStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === DELETE COMPANY DOCUMENT ===
    builder.addCase(deleteCompanyDocument.pending, (state, action) => {
      state.error = null;
      state.deleteDocumentStatus = fetchStatuses.pending;
      state.deletingDocumentId = action.meta.arg;
    });
    builder.addCase(deleteCompanyDocument.fulfilled, (state) => {
      state.deleteDocumentStatus = fetchStatuses.success;
      state.deletingDocumentId = null;
    });
    builder.addCase(deleteCompanyDocument.rejected, (state, action) => {
      state.deleteDocumentStatus = fetchStatuses.rejected;
      state.deletingDocumentId = null;
      state.error = action.error;
    });

    // === GET ATTACHMENT ===
    builder.addCase(getAttachment.pending, (state) => {
      state.error = null;
      state.getAttachmentStatus = fetchStatuses.pending;
    });
    builder.addCase(getAttachment.fulfilled, (state) => {
      state.getAttachmentStatus = fetchStatuses.success;
    });
    builder.addCase(getAttachment.rejected, (state, action) => {
      state.getAttachmentStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === RESEND ACTIVATION LINK ===
    builder.addCase(resendActivationLink.pending, (state) => {
      state.error = null;
      state.resendActivationLinkStatus = fetchStatuses.pending;
    });
    builder.addCase(resendActivationLink.fulfilled, (state, action) => {
      state.resendActivationLinkStatus = fetchStatuses.success;
      state.lastResendActivationLinkResponse = action.payload.data;
    });
    builder.addCase(resendActivationLink.rejected, (state, action) => {
      state.resendActivationLinkStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH COMPANY WITHDRAW REQUESTS ===
    builder.addCase(fetchCompanyWithdrawRequests.pending, (state) => {
      state.error = null;
      state.fetchCompanyWithdrawRequestsStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchCompanyWithdrawRequests.fulfilled,
      (state, action: PayloadAction<FetchCompanyWithdrawRequestsResponse>) => {
        state.fetchCompanyWithdrawRequestsStatus = fetchStatuses.success;
        state.companyWithdrawRequests = action.payload.data.items;
        state.totalCompanyWithdrawRequests = action.payload.data.total;
      },
    );
    builder.addCase(fetchCompanyWithdrawRequests.rejected, (state, action) => {
      state.fetchCompanyWithdrawRequestsStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === APPROVE COMPANY WITHDRAW REQUEST ===
    builder.addCase(approveCompanyWithdrawRequest.pending, (state) => {
      state.error = null;
      state.approveCompanyWithdrawRequestStatus = fetchStatuses.pending;
    });
    builder.addCase(
      approveCompanyWithdrawRequest.fulfilled,
      (state, action: PayloadAction<ApproveCompanyWithdrawRequestResponse>) => {
        state.approveCompanyWithdrawRequestStatus = fetchStatuses.success;
        // Update the specific withdraw request in the list
        const updatedRequest = action.payload.data;
        const index = state.companyWithdrawRequests.findIndex((req) => req.id === updatedRequest.id);
        if (index !== -1) {
          state.companyWithdrawRequests[index] = updatedRequest;
        }
      },
    );
    builder.addCase(approveCompanyWithdrawRequest.rejected, (state, action) => {
      state.approveCompanyWithdrawRequestStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH COMPANY WITHDRAW REQUEST ===
    builder.addCase(fetchCompanyWithdrawRequest.pending, (state) => {
      state.error = null;
      state.fetchCompanyWithdrawRequestStatus = fetchStatuses.pending;
    });
    builder.addCase(
      fetchCompanyWithdrawRequest.fulfilled,
      (state, action: PayloadAction<FetchCompanyWithdrawRequestResponse>) => {
        state.fetchCompanyWithdrawRequestStatus = fetchStatuses.success;
        state.editCompanyWithdrawRequest = action.payload.data;
      },
    );
    builder.addCase(fetchCompanyWithdrawRequest.rejected, (state, action) => {
      state.fetchCompanyWithdrawRequestStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === CREATE COMPANY WITHDRAW REQUEST ===
    builder.addCase(createCompanyWithdrawRequest.pending, (state) => {
      state.error = null;
      state.createCompanyWithdrawRequestStatus = fetchStatuses.pending;
    });
    builder.addCase(
      createCompanyWithdrawRequest.fulfilled,
      (state, action: PayloadAction<CreateCompanyWithdrawRequestResponse>) => {
        state.createCompanyWithdrawRequestStatus = fetchStatuses.success;
        // Add the new withdraw request to the beginning of the list
        state.companyWithdrawRequests.unshift(action.payload.data);
        state.totalCompanyWithdrawRequests += 1;
      },
    );
    builder.addCase(createCompanyWithdrawRequest.rejected, (state, action) => {
      state.createCompanyWithdrawRequestStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPDATE COMPANY WITHDRAW REQUEST ===
    builder.addCase(updateCompanyWithdrawRequest.pending, (state) => {
      state.error = null;
      state.updateCompanyWithdrawRequestStatus = fetchStatuses.pending;
    });
    builder.addCase(
      updateCompanyWithdrawRequest.fulfilled,
      (state, action: PayloadAction<UpdateCompanyWithdrawRequestResponse>) => {
        state.updateCompanyWithdrawRequestStatus = fetchStatuses.success;
        // Update the specific withdraw request in the list
        const updatedRequest = action.payload.data;
        const index = state.companyWithdrawRequests.findIndex((req) => req.id === updatedRequest.id);
        if (index !== -1) {
          state.companyWithdrawRequests[index] = updatedRequest;
        }
        // Clear the edit request
        state.editCompanyWithdrawRequest = null;
      },
    );
    builder.addCase(updateCompanyWithdrawRequest.rejected, (state, action) => {
      state.updateCompanyWithdrawRequestStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === REJECT COMPANY WITHDRAW REQUEST ===
    builder.addCase(rejectCompanyWithdrawRequest.pending, (state) => {
      state.error = null;
      state.rejectCompanyWithdrawRequestStatus = fetchStatuses.pending;
    });
    builder.addCase(
      rejectCompanyWithdrawRequest.fulfilled,
      (state, action: PayloadAction<RejectCompanyWithdrawRequestResponse>) => {
        state.rejectCompanyWithdrawRequestStatus = fetchStatuses.success;
        // Update the specific withdraw request in the list
        const updatedRequest = action.payload.data;
        const index = state.companyWithdrawRequests.findIndex((req) => req.id === updatedRequest.id);
        if (index !== -1) {
          state.companyWithdrawRequests[index] = updatedRequest;
        }
      },
    );
    builder.addCase(rejectCompanyWithdrawRequest.rejected, (state, action) => {
      state.rejectCompanyWithdrawRequestStatus = fetchStatuses.rejected;
      state.error = action.error;
    });
  },
});

export const companyReducer = companySlice.reducer;
